/**
 * Static analysis primitives for GSAP timeline scripts and CSS transforms, shared by the
 * animation-correctness runtime lint rules. Ported from the codeo composition linter. These operate
 * on script/CSS source text and never execute it.
 */

// Advance past a string literal that starts at `start` (the opening quote). Returns the index just
// after the closing quote, or -1 if unterminated.
function skipString(text: string, start: number, quote: string): number {
    let i = start + 1;
    while (i < text.length) {
        const c = text[i];
        if (c === '\\') {
            i += 2;
            continue;
        }
        if (c === quote) return i + 1;
        i++;
    }
    return -1;
}

/**
 * Given the index just inside an opening `(`, return the index of the balanced closing `)`,
 * skipping over string literals and comments. Returns -1 if unbalanced.
 */
export function findMatchingClose(text: string, start: number): number {
    let depth = 1;
    let i = start;
    while (i < text.length) {
        const c = text[i];
        if (c === '"' || c === "'" || c === '`') {
            const next = skipString(text, i, c);
            if (next === -1) return -1;
            i = next;
            continue;
        }
        if (c === '/' && text[i + 1] === '/') {
            const nl = text.indexOf('\n', i);
            if (nl === -1) return -1;
            i = nl + 1;
            continue;
        }
        if (c === '/' && text[i + 1] === '*') {
            const close = text.indexOf('*/', i + 2);
            if (close === -1) return -1;
            i = close + 2;
            continue;
        }
        if (c === '(') depth++;
        else if (c === ')') {
            depth--;
            if (depth === 0) return i;
        }
        i++;
    }
    return -1;
}

/** The first `{...}` object literal in an argument list (the tween's vars), or null. */
export function firstObjectLiteral(args: string): string | null {
    let i = 0;
    while (i < args.length) {
        const c = args[i];
        if (c === '"' || c === "'" || c === '`') {
            const next = skipString(args, i, c);
            if (next === -1) return null;
            i = next;
            continue;
        }
        if (c === '{') {
            let depth = 1;
            let j = i + 1;
            while (j < args.length) {
                const d = args[j];
                if (d === '"' || d === "'" || d === '`') {
                    const next = skipString(args, j, d);
                    if (next === -1) return null;
                    j = next;
                    continue;
                }
                if (d === '{') depth++;
                else if (d === '}') {
                    depth--;
                    if (depth === 0) return args.slice(i, j + 1);
                }
                j++;
            }
            return null;
        }
        i++;
    }
    return null;
}

/**
 * The first top-level arg, if it's a plain string-literal selector ('#x' / ".y"). Returns null for
 * templates, variables, or arrays — the selector is then unknown.
 */
export function firstStringArg(args: string): string | null {
    let i = 0;
    let depth = 0;
    while (i < args.length) {
        const c = args[i];
        if (c === '"' || c === "'" || c === '`') {
            const next = skipString(args, i, c);
            if (next === -1) return null;
            i = next;
            continue;
        }
        if (c === '(' || c === '{' || c === '[') depth++;
        else if (c === ')' || c === '}' || c === ']') depth--;
        else if (c === ',' && depth === 0) break;
        i++;
    }
    const lit = args
        .slice(0, i)
        .trim()
        .match(/^['"]([^'"]+)['"]$/);
    return lit ? lit[1] : null;
}

/**
 * Transform CHANNELS, the unit of conflict. GSAP decomposes an element's computed CSS transform into
 * per-channel components (x/y, scale, rotation) on first touch and preserves the channels it isn't
 * animating — so a scale tween on a translate(-50%,-50%)-centered element is fine. The clobber
 * happens when a tween applies an EXPLICIT value on a channel the CSS transform also provides.
 */
export const GSAP_PROP_TO_CHANNEL: Record<string, string> = {
    x: 'x',
    xPercent: 'x',
    y: 'y',
    yPercent: 'y',
    scale: 'scale',
    scaleX: 'scale',
    scaleY: 'scale',
    rotation: 'rotation',
    rotationX: 'rotation',
    rotationY: 'rotation',
    skewX: 'skew',
    skewY: 'skew',
};

export const GSAP_TRANSFORM_PROP_RE = new RegExp(
    `[{,]\\s*(${Object.keys(GSAP_PROP_TO_CHANNEL).join('|')})\\s*:`,
    'g',
);

/** The transform channels a CSS `transform` value contributes. `matrix()` folds every channel. */
export function cssTransformChannels(value: string): Set<string> {
    const channels = new Set<string>();
    if (/matrix/.test(value))
        ['x', 'y', 'scale', 'rotation', 'skew'].forEach((c) => channels.add(c));
    if (/translate(3d)?\(/.test(value)) ['x', 'y'].forEach((c) => channels.add(c));
    if (/translateX/.test(value)) channels.add('x');
    if (/translateY/.test(value)) channels.add('y');
    if (/scale(3d|X|Y)?\(/.test(value)) channels.add('scale');
    if (/rotate/.test(value)) channels.add('rotation');
    if (/skew/.test(value)) channels.add('skew');
    return channels;
}

/** The transform channels a GSAP tween's vars object writes explicitly. */
export function tweenChannels(args: string): Set<string> {
    const channels = new Set<string>();
    GSAP_TRANSFORM_PROP_RE.lastIndex = 0;
    let m: RegExpExecArray | null;
    while ((m = GSAP_TRANSFORM_PROP_RE.exec(args)) !== null) {
        channels.add(GSAP_PROP_TO_CHANNEL[m[1]]);
    }
    return channels;
}
